import { useState, useCallback, useEffect, useRef } from "react";
import { userService } from "../services";
import toast from "react-hot-toast";

export const useUsers = (params = {}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [selectedAvatar, setSelectedAvatar] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(null);

  // Data state
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  // Loading states for mutations
  const [isCreating, setIsCreating] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isToggling, setIsToggling] = useState(false);
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);
  const [isDeletingAvatar, setIsDeletingAvatar] = useState(false);

  // Debounce and throttling
  const debounceTimeoutRef = useRef(null);
  const lastRequestTimeRef = useRef(0);
  const retryCountRef = useRef(0);
  const REQUEST_THROTTLE_MS = 1000; // Minimum 1 second between requests
  const MAX_RETRIES = 3;

  // Fetch users with throttling
  const fetchUsers = useCallback(async () => {
    const now = Date.now();
    const timeSinceLastRequest = now - lastRequestTimeRef.current;

    // Throttle requests to prevent rate limiting
    if (timeSinceLastRequest < REQUEST_THROTTLE_MS) {
      console.log("Request throttled, waiting...");
      return;
    }

    lastRequestTimeRef.current = now;
    setIsLoading(true);

    try {
      const response = await userService.getAll({
        page: currentPage,
        limit: 10,
        search: searchTerm,
        ...params,
      });

      setUsers(response.data.users || []);
      setTotalPages(response.data.pagination?.pages || 1);
      setTotalItems(response.data.pagination?.total || 0);
    } catch (error) {
      // Handle rate limiting with retry
      if (error.response?.status === 429) {
        if (retryCountRef.current < MAX_RETRIES) {
          retryCountRef.current++;
          const retryDelay = Math.pow(2, retryCountRef.current) * 1000; // Exponential backoff
          toast.error(
            `Rate limited. Retrying in ${retryDelay / 1000} seconds...`
          );

          setTimeout(() => {
            fetchUsers();
          }, retryDelay);
          return;
        } else {
          toast.error("Too many requests. Please wait a moment and try again.");
          retryCountRef.current = 0;
          return;
        }
      }

      // Reset retry count on other errors
      retryCountRef.current = 0;

      toast.error(
        error.response?.data?.message ||
          error.message ||
          "Failed to fetch users"
      );
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, searchTerm]);

  // Debounced search effect
  useEffect(() => {
    // Clear existing timeout
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    // Set new timeout for search
    debounceTimeoutRef.current = setTimeout(() => {
      fetchUsers();
    }, 500); // 500ms debounce for search

    // Cleanup timeout on unmount
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, [searchTerm]);

  // Load users on mount and when page changes (no debounce for page changes)
  useEffect(() => {
    fetchUsers();
  }, [currentPage]);

  // Manual refresh function that doesn't depend on useEffect
  const refreshUsers = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await userService.getAll({
        page: currentPage,
        limit: 10,
        search: searchTerm,
        ...params,
      });

      setUsers(response.data.users || []);
      setTotalPages(response.data.pagination?.pages || 1);
      setTotalItems(response.data.pagination?.total || 0);
    } catch (error) {
      toast.error(
        error.response?.data?.message ||
          error.message ||
          "Failed to fetch users"
      );
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, searchTerm, params]);

  // Create user
  const createUser = useCallback(
    async (data) => {
      setIsCreating(true);
      try {
        await userService.create(data);
        toast.success("User created successfully!");
        setShowModal(false);
        await fetchUsers(); // Refresh the list
      } catch (error) {
        toast.error(
          error.response?.data?.message ||
            error.message ||
            "Failed to create user"
        );
      } finally {
        setIsCreating(false);
      }
    },
    [fetchUsers]
  );

  // Update user
  const updateUser = useCallback(
    async (id, data) => {
      setIsUpdating(true);
      try {
        await userService.update(id, data);
        toast.success("User updated successfully!");
        setShowModal(false);
        setEditingUser(null);
        await fetchUsers(); // Refresh the list
      } catch (error) {
        toast.error(
          error.response?.data?.message ||
            error.message ||
            "Failed to update user"
        );
      } finally {
        setIsUpdating(false);
      }
    },
    [fetchUsers]
  );

  // Delete user
  const deleteUser = useCallback(
    async (id) => {
      setIsDeleting(true);
      try {
        await userService.delete(id);
        toast.success("User deleted successfully!");
        await fetchUsers(); // Refresh the list
      } catch (error) {
        toast.error(
          error.response?.data?.message ||
            error.message ||
            "Failed to delete user"
        );
      } finally {
        setIsDeleting(false);
      }
    },
    [fetchUsers]
  );

  // Toggle status
  const toggleStatus = useCallback(
    async (id) => {
      setIsToggling(true);
      try {
        await userService.toggleStatus(id);
        toast.success("User status updated successfully!");
        await fetchUsers(); // Refresh the list
      } catch (error) {
        toast.error(
          error.response?.data?.message ||
            error.message ||
            "Failed to update user status"
        );
      } finally {
        setIsToggling(false);
      }
    },
    [fetchUsers]
  );

  // Upload avatar
  const uploadAvatar = useCallback(
    async (id, avatarFile) => {
      setIsUploadingAvatar(true);
      try {
        await userService.uploadAvatar(id, avatarFile);
        toast.success("User avatar uploaded successfully!");
        setSelectedAvatar(null);
        setAvatarPreview(null);
        await fetchUsers(); // Refresh the list
      } catch (error) {
        toast.error(
          error.response?.data?.message ||
            error.message ||
            "Failed to upload avatar"
        );
      } finally {
        setIsUploadingAvatar(false);
      }
    },
    [fetchUsers]
  );

  // Delete avatar
  const deleteAvatar = useCallback(
    async (id) => {
      setIsDeletingAvatar(true);
      try {
        await userService.deleteAvatar(id);
        toast.success("User avatar deleted successfully!");
        await fetchUsers(); // Refresh the list
      } catch (error) {
        toast.error(
          error.response?.data?.message ||
            error.message ||
            "Failed to delete avatar"
        );
      } finally {
        setIsDeletingAvatar(false);
      }
    },
    [fetchUsers]
  );

  // Handlers
  const handleSubmit = useCallback(
    (data) => {
      if (editingUser) {
        updateUser(editingUser.id, data);
      } else {
        createUser(data);
      }
    },
    [editingUser, updateUser, createUser]
  );

  const handleEdit = useCallback((user) => {
    setEditingUser(user);
    setShowModal(true);
  }, []);

  const handleDelete = useCallback(
    (user) => {
      if (window.confirm("Are you sure you want to delete this user?")) {
        deleteUser(user.id);
      }
    },
    [deleteUser]
  );

  const handleToggleStatus = useCallback(
    (user) => {
      toggleStatus(user.id);
    },
    [toggleStatus]
  );

  const handleCloseModal = useCallback(() => {
    setShowModal(false);
    setEditingUser(null);
    setSelectedAvatar(null);
    setAvatarPreview(null);
  }, []);

  const handleAvatarSelect = useCallback(
    (event, userId) => {
      const file = event.target.files[0];
      if (file) {
        setSelectedAvatar(file);
        const reader = new FileReader();
        reader.onload = (e) => setAvatarPreview(e.target.result);
        reader.readAsDataURL(file);

        // Auto-upload only if we're in the table view (userId provided)
        if (userId) {
          uploadAvatar(userId, file);
        }
      }
    },
    [uploadAvatar]
  );

  const handleAvatarUpload = useCallback(
    (userId) => {
      if (selectedAvatar) {
        uploadAvatar(userId, selectedAvatar);
      }
    },
    [selectedAvatar, uploadAvatar]
  );

  const handleAvatarDelete = useCallback(
    (userId) => {
      if (window.confirm("Are you sure you want to delete this user avatar?")) {
        deleteAvatar(userId);
      }
    },
    [deleteAvatar]
  );

  const handleRemoveAvatarPreview = useCallback(() => {
    setSelectedAvatar(null);
    setAvatarPreview(null);
  }, []);

  const handlePageChange = useCallback((page) => {
    setCurrentPage(page);
  }, []);

  const handleClearFilters = useCallback(() => {
    setSearchTerm("");
  }, []);

  // Manual retry function for rate-limited requests
  const handleRetry = useCallback(() => {
    retryCountRef.current = 0;
    fetchUsers();
  }, [fetchUsers]);

  // Filter users based on search term and hide users with role ID = 1
  const filteredUsers = users.filter(
    (user) =>
      // Hide users with role ID = 1
      user.roleId !== 1 &&
      // Apply search filter
      (user.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.lastName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.phone?.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  return {
    // State
    searchTerm,
    setSearchTerm,
    currentPage,
    showModal,
    setShowModal,
    editingUser,
    selectedAvatar,
    avatarPreview,

    // Data
    users: filteredUsers,
    isLoading,
    totalPages,
    totalItems,

    // Loading states
    isCreating,
    isUpdating,
    isDeleting,
    isToggling,
    isUploadingAvatar,
    isDeletingAvatar,

    // Handlers
    handleSubmit,
    handleEdit,
    handleDelete,
    handleToggleStatus,
    handleCloseModal,
    handleAvatarSelect,
    handleAvatarUpload,
    handleAvatarDelete,
    handleRemoveAvatarPreview,
    handlePageChange,
    handleClearFilters,
    handleRetry,

    // Direct functions
    createUser,
    updateUser,
    deleteUser,
    toggleStatus,
    uploadAvatar,
    deleteAvatar,
    fetchUsers,
    refreshUsers,
  };
};

export default useUsers;
